import { routeIncentive } from "@/setup/routes/incentive.routes";
import type { ShiftIncentiveData } from "@/pages/incentive/models/ShiftIncentiveData";
import {
	SHIFT_CONFIG_COLORS,
	type KPIThresholds,
} from "@/shared/components/KPICard";
import {
	Box,
	CircularProgress,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import { Link } from "react-router-dom";

interface Props {
	data: ShiftIncentiveData | null;
	thresholds: KPIThresholds;
}

const getValueColor = (value: number, thresholds: KPIThresholds): string => {
	if (value >= thresholds.greenHard) return SHIFT_CONFIG_COLORS.greenHard;
	if (value >= thresholds.greenNormal) return SHIFT_CONFIG_COLORS.greenNormal;
	if (value >= thresholds.greenSoft) return SHIFT_CONFIG_COLORS.greenSoft;
	if (value >= thresholds.yellow) return SHIFT_CONFIG_COLORS.yellow;
	if (value >= thresholds.orange) return SHIFT_CONFIG_COLORS.orange;
	return SHIFT_CONFIG_COLORS.red;
};

const formatTons = (value: number | null): string => {
	if (value == null || value === 0) return "-";
	return value.toLocaleString("en-US", {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	});
};

const formatPercent = (value: number | null): string =>
	value != null ? `${value.toFixed(1)}%` : "-";

export const IncentiveCard = ({ data, thresholds }: Props) => {
	const incColor =
		data?.IncPercent != null
			? getValueColor(data.IncPercent, thresholds)
			: "#999";
	const cappedPct =
		data?.IncPercent != null ? Math.min(data.IncPercent, 100) : 0;

	const circleSize = 140;
	const strokeWidth = 8;

	const details = data
		? [
				{ label: "Target Tons", value: formatTons(data.TargetTons) },
				{
					label: "Finished Tons",
					value: data.FinishedTons ? formatTons(data.FinishedTons) : "-",
				},
			]
		: [];

	const paddedDetails = [...details];
	while (paddedDetails.length < 3) {
		paddedDetails.push({ label: "", value: "" });
	}

	return (
		<Paper
			elevation={0}
			sx={{
				p: 1,
				height: "100%",
				width: "100%",
				display: "flex",
				flexDirection: "column",
				border: "1px solid #ddd",
				borderRadius: 1,
				backgroundColor: "#fff",
				userSelect: "none",
			}}
		>
			<Typography
				component={Link}
				to={routeIncentive}
				sx={{
					fontWeight: 600,
					color: "#1565c0",
					fontSize: "0.85rem",
					lineHeight: 1,
					textDecoration: "none",
					"&:hover": { textDecoration: "underline" },
				}}
			>
				Incentive
			</Typography>

			{!data ? (
				<Box
					sx={{
						flex: 1,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Typography sx={{ fontSize: "0.7rem", color: "#999" }}>
						No data
					</Typography>
				</Box>
			) : (
				<>
					<Box
						sx={{
							height: 150,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Tooltip title="Shift incentive percentage" placement="top">
							<Box sx={{ position: "relative", display: "inline-flex" }}>
								<CircularProgress
									variant="determinate"
									value={100}
									size={circleSize}
									thickness={strokeWidth / 2}
									sx={{
										color: "#e0e0e0",
										position: "absolute",
									}}
								/>
								<CircularProgress
									variant="determinate"
									value={cappedPct}
									size={circleSize}
									thickness={strokeWidth / 2}
									sx={{
										color: incColor,
										transform: "rotate(-90deg) !important",
										"& .MuiCircularProgress-circle": {
											strokeLinecap: "round",
											transition: "stroke-dashoffset 1s linear",
										},
									}}
								/>
								<Box
									sx={{
										position: "absolute",
										top: 0,
										left: 0,
										bottom: 0,
										right: 0,
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<Typography
										sx={{
											fontSize: "1.2rem",
											fontWeight: 700,
											color: incColor,
											lineHeight: 1,
										}}
									>
										{formatPercent(data.IncPercent)}
									</Typography>
									<Typography
										sx={{
											fontSize: "0.65rem",
											color: "#666",
											mt: 0.5,
										}}
									>
										Inc%
									</Typography>
								</Box>
							</Box>
						</Tooltip>
					</Box>

					<Box
						sx={{
							flex: 1,
							display: "flex",
							flexDirection: "column",
							justifyContent: "flex-end",
						}}
					>
						<Box sx={{ pt: 0.5, borderTop: "1px solid #ddd" }}>
							{paddedDetails.map((detail) => (
								<Box
									key={detail.label}
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										minHeight: 16,
										userSelect: "none",
									}}
								>
									<Typography sx={{ fontSize: "0.6rem", color: "#333" }}>
										{detail.label}
									</Typography>
									<Typography
										sx={{
											fontSize: "0.6rem",
											fontWeight: 600,
											color: "#333",
										}}
									>
										{detail.value}
									</Typography>
								</Box>
							))}
						</Box>
					</Box>
				</>
			)}
		</Paper>
	);
};
